import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App } from "antd";
import { useTranslation } from "react-i18next";
import { usePaginatedQuery } from "@/shared/hooks/usePaginatedQuery";
import { getErrorMessage } from "@/shared/utils/getErrorMessage";
import { departmentsService } from "../services/departments.service";
import type { CreateDepartmentInput, DepartmentListItem, UpdateDepartmentInput } from "../types";

/**
 * `GET /departments` sahifalanmagan va TEKIS ro'yxat qaytaradi — daraxt
 * `buildTree` orqali `DepartmentTreeSelect` ichida quriladi (sahifaning
 * o'zi Explorer jadvalini ko'rsatadi). Shu sabab `usePaginatedQuery` emas,
 * oddiy `useQuery` ishlatiladi.
 *
 * ⚠️ `enabled` — `DepartmentsExplorerTable` tekis qidiruvda yo'l subtitle'i
 * uchun id→nom xaritasi kerak bo'lganda ochadi (spec §9); boshqa
 * chaqiruvchilar (`DepartmentsPage`, `DepartmentFormModal`, ...) standart
 * bo'yicha doim yoqilgan holda qoladi.
 */
export function useDepartmentsList(options: { enabled?: boolean } = {}) {
	return useQuery({
		queryKey: ["departments"],
		queryFn: () => departmentsService.list(),
		enabled: options.enabled ?? true,
	});
}

function useDepartmentsMutation<TVariables>(
	mutationFn: (variables: TVariables) => Promise<unknown>,
	successMessage: string,
	errorFallback: string
) {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["departments"] });
			message.success(successMessage);
		},
		onError: (error) => {
			// Ko'chirishda backend aylanma/chuqurlik xatosini aniq matn bilan
			// qaytaradi (masalan "aylanma hosil bo'ladi") — umumiy xabar emas,
			// aynan shu xabar ko'rsatilishi kerak.
			message.error(getErrorMessage(error, errorFallback));
		},
	});
}

export function useCreateDepartment() {
	const { t } = useTranslation("departments");
	return useDepartmentsMutation(
		(input: CreateDepartmentInput) => departmentsService.create(input),
		t("messages.created"),
		t("messages.createFailed")
	);
}

export function useUpdateDepartment() {
	const { t } = useTranslation("departments");
	return useDepartmentsMutation(
		({ id, input }: { id: string; input: UpdateDepartmentInput }) =>
			departmentsService.update(id, input),
		t("messages.updated"),
		t("messages.updateFailed")
	);
}

/** Ko'chirish — xuddi shu `PATCH { parentId }`, faqat alohida muvaffaqiyat xabari bilan. */
export function useMoveDepartment() {
	const { t } = useTranslation("departments");
	return useDepartmentsMutation(
		({ id, parentId }: { id: string; parentId: string | null }) =>
			departmentsService.update(id, { parentId }),
		t("messages.moved"),
		t("messages.moveFailed")
	);
}

export function useDeleteDepartment() {
	const { t } = useTranslation("departments");
	return useDepartmentsMutation(
		(id: string) => departmentsService.remove(id),
		t("messages.deleted"),
		t("messages.deleteFailed")
	);
}

/**
 * Arxivlash — `POST /departments/{id}/archive`.
 *
 * ⚠️ `useDepartmentsMutation` orqali, yalang'och `useMutation` EMAS: backend
 * faol bolasi bor (`hasActiveChildren`) yoki allaqachon arxivlangan
 * (`alreadyArchived`) bo'limni 409 bilan rad etadi va chaqiruvchi
 * (Sozlamalar yorlig'i) `mutate(id)` ni handler'siz chaqiradi — xato
 * xabarisiz rad etish JIMGINA bo'lardi. `["departments"]` prefiksi
 * `["departments","detail",id]` va `summary` keshini ham yangilaydi.
 */
export function useArchiveDepartment() {
	const { t } = useTranslation("departments");
	return useDepartmentsMutation(
		(id: string) => departmentsService.archive(id),
		t("messages.archived"),
		t("messages.archiveFailed")
	);
}

/**
 * Arxivdan chiqarish — `POST /departments/{id}/unarchive`. Rad etish (ota
 * bo'lim arxivda — 409 `unarchiveParentArchived`) xabari shu yerda
 * ko'rsatiladi, `useArchiveDepartment` bilan bir xil sabab.
 */
export function useUnarchiveDepartment() {
	const { t } = useTranslation("departments");
	return useDepartmentsMutation(
		(id: string) => departmentsService.unarchive(id),
		t("messages.unarchived"),
		t("messages.unarchiveFailed")
	);
}

/** Profil sahifasi — `GET /departments/{id}` (`ancestors` bilan). */
export function useDepartment(id: string | undefined) {
	return useQuery({
		queryKey: ["departments", "detail", id],
		queryFn: () => departmentsService.getDetail(id as string),
		enabled: Boolean(id),
	});
}

/** Profil sahifasi agregati — `GET /departments/{id}/summary`. */
export function useDepartmentSummary(id: string | undefined) {
	return useQuery({
		queryKey: ["departments", "summary", id],
		queryFn: () => departmentsService.getSummary(id as string),
		enabled: Boolean(id),
	});
}

/**
 * Explorer jadvali uchun — `GET /departments/children`. `parentId`
 * BERILMASA ILDIZ tugunlarni qaytaradi (backend §Task 3, 2-reja).
 *
 * ⚠️ `queryKey`ga `parentId` KIRITILADI — aks holda "Bosh ofis"dan
 * "Andijon viloyat filiali"ga o'tilganda TanStack Query eski keshdan
 * bir xil kalitni qaytarib, jadval yangilanmasdan qolardi.
 */
export function useDepartmentsExplorer(params: {
	parentId?: string;
	q?: string;
	isActive?: boolean;
}) {
	return usePaginatedQuery<DepartmentListItem>({
		queryKey: ["departments", "children", params.parentId ?? "root", params.q, params.isActive],
		url: "/departments/children",
		params: {
			parentId: params.parentId,
			q: params.q,
			isActive: params.isActive,
		},
	});
}
